import React, { useState, useEffect } from "react";
import ViewHeader from "../viewHeader/viewHeader";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import apiService from "../../utils/api";
import { useNavigate } from "react-router-dom";

const SAMPLE_LABEL = {
  className: "Junior Infants",
  studentName: "Sample Student",
  foodItemName: "Chicken Curry",
  allergens: "Gluten, Dairy",
  orderDate: "2026-04-29",
};

const LabelConfig = () => {
  const navigate = useNavigate();
  const [labelWidth, setLabelWidth] = useState("");
  const [labelHeight, setLabelHeight] = useState("");
  const [labelHeaderFontSize, setLabelHeaderFontSize] = useState(14);
  const [labelBodyFontSize, setLabelBodyFontSize] = useState(11);
  const [configId, setConfigId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const authToken = sessionStorage.getItem("authToken");
        if (authToken) {
          const response = await apiService.getLabelConfig(authToken);
          const config = response.data;
          setConfigId(config.id);
          setLabelWidth(config.labelWidth);
          setLabelHeight(config.labelHeight);
          setLabelHeaderFontSize(config.labelHeaderFontSize ?? 14);
          setLabelBodyFontSize(config.labelBodyFontSize ?? 11);
        } else {
          navigate("/");
        }
      } catch (error) {
        console.error("Error fetching label config:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const authToken = sessionStorage.getItem("authToken");
    const store = sessionStorage.getItem("store");
    if (!authToken) {
      navigate("/");
      return;
    }
    setSaving(true);
    try {
      await apiService.updateLabelConfig(
        authToken,
        configId,
        labelWidth,
        labelHeight,
        store,
        labelHeaderFontSize,
        labelBodyFontSize,
      );
      navigate("/settings");
    } catch (error) {
      console.error("Error updating label config:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const headerPx = Number(labelHeaderFontSize) || 0;
  const bodyPx = Number(labelBodyFontSize) || 0;

  return (
    <Container>
      <ViewHeader screenName={"Label Configuration"} />
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          component={"form"}
          className="custom-box"
          onSubmit={handleSubmit}
        >
          <TextField
            label="Label Width (cm)"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={labelWidth}
            onChange={(e) => setLabelWidth(e.target.value)}
          />
          <TextField
            label="Label Height (cm)"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={labelHeight}
            onChange={(e) => setLabelHeight(e.target.value)}
          />
          <TextField
            label="Class Font Size (px)"
            helperText="Size of the class line at the top of the label"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            type="number"
            inputProps={{ step: "1", min: "6", max: "48" }}
            value={labelHeaderFontSize}
            onChange={(e) => setLabelHeaderFontSize(e.target.value)}
          />
          <TextField
            label="Body Font Size (px)"
            helperText="Size of student name, food item, allergens, and date"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            type="number"
            inputProps={{ step: "1", min: "6", max: "48" }}
            value={labelBodyFontSize}
            onChange={(e) => setLabelBodyFontSize(e.target.value)}
          />

          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 1 }}>
            Preview ({labelWidth}cm × {labelHeight}cm):
          </Typography>
          <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
            <Paper
              variant="outlined"
              style={{
                width: `${labelWidth}cm`,
                height: `${labelHeight}cm`,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                fontFamily: "'Roboto', Helvetica, sans-serif",
              }}
            >
              <Box display="flex" flexDirection="column" style={{ marginTop: "6px", marginLeft: "20px" }}>
                <span
                  style={{
                    fontSize: `${headerPx}px`,
                    fontWeight: 600,
                    lineHeight: `${Math.max(headerPx + 6, 14)}px`,
                  }}
                >
                  {SAMPLE_LABEL.className}
                </span>
                <span style={{ fontSize: `${bodyPx}px`, fontWeight: 400, lineHeight: `${bodyPx}px`, marginTop: "2px" }}>
                  {SAMPLE_LABEL.studentName}
                </span>
                <span style={{ fontSize: `${bodyPx}px`, fontWeight: 400, lineHeight: `${bodyPx}px`, marginTop: "2px" }}>
                  {SAMPLE_LABEL.foodItemName}
                </span>
                <span style={{ fontSize: `${bodyPx}px`, fontWeight: 400, lineHeight: `${bodyPx}px`, marginTop: "2px" }}>
                  Contains Allergens: <span style={{ fontWeight: 600 }}>{SAMPLE_LABEL.allergens}</span>
                </span>
                <span style={{ fontSize: `${bodyPx}px`, fontWeight: 400, lineHeight: `${bodyPx}px`, marginTop: "2px" }}>
                  Order Date: {SAMPLE_LABEL.orderDate}
                </span>
              </Box>
            </Paper>
          </Box>

          <Button variant="contained" color="primary" fullWidth type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Container>
    </Container>
  );
};

export default LabelConfig;
